import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  MatchQuery,
  QueryContainer,
  RangeQuery,
} from '@opensearch-project/opensearch/api/_types/_common.query_dsl';

import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';

import { useOpenSearchContext } from './useOpenSearchContext';
import { buildFacetQueryFilters, sanitizeQueryContainer } from '../utils';

const useRefinementsHelpers = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { queryFiltersByFacets, updateQueryFiltersByFacets } =
    useOpenSearchContext();

  const activeFilters = useMemo(
    () =>
      Object.entries(queryFiltersByFacets).reduce((acc, [attribute, query]) => {
        if (attribute === 'id') return acc; // Skip 'id' attribute
        if (sanitizeQueryContainer(query as any)) {
          const refinementItems =
            attribute === PRODUCT_FILTER_ATTRIBUTES._ON_SALE
              ? [true]
              : getRefinementItemsFromQuery(query as QueryContainer, attribute);
          const numericItems = getNumericItemsFromQuery(
            query as QueryContainer,
            attribute,
          );

          acc[attribute] = {
            items: refinementItems ?? numericItems,
            type: refinementItems ? 'refinement' : 'numeric',
          };
        }
        return acc;
      }, {} as Record<string, { items: (MatchQuery | RangeQuery)[]; type: 'refinement' | 'numeric' }>), // Return an empty object if no filters are active
    [queryFiltersByFacets],
  );

  const activeFiltersCount = useMemo(
    () =>
      Object.values(activeFilters).reduce(
        (count, { items }) => count + items.length,
        0,
      ),
    [activeFilters],
  );

  const clearAllFilters = () => {
    updateQueryFiltersByFacets(prev =>
      Object.fromEntries(
        Object.keys(prev).map(attribute => [
          attribute,
          buildFacetQueryFilters(attribute, []),
        ]),
      ),
    );

    const params = new URLSearchParams(searchParams.toString());
    Object.keys(queryFiltersByFacets).forEach(attribute => {
      params.delete(attribute);
    });

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  return { activeFilters, activeFiltersCount, clearAllFilters };
};

const getNumericItemsFromQuery = (
  query: QueryContainer,
  attribute: string,
): RangeQuery[] => [
  ((query as QueryContainer)?.bool?.filter as QueryContainer)?.range?.[
    attribute
  ] as RangeQuery,
];

const getRefinementItemsFromQuery = (
  query: QueryContainer,
  attribute: string,
): MatchQuery[] =>
  ((query as QueryContainer)?.bool?.should as QueryContainer[])?.map(
    item => item.match?.[attribute],
  );

export { useRefinementsHelpers };
